import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ApiResponse } from '../../models/api.model';
import { ServiceCharge } from '../models/service-charge.model';
import { ServiceChargesService } from '../services/service-charges.service';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-service-charges-modal',
  templateUrl: './service-charges-modal.component.html',
  styleUrls: ['./service-charges-modal.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    BoDialogComponent,
    ButtonComponent,
  ],
})
export class ServiceChargesModalComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef =
    inject<BrnDialogRef<ServiceCharge[]>>(BrnDialogRef);
  private readonly serviceChargesService = inject(ServiceChargesService);
  private readonly toastr = inject(ToastrService);

  private _selectedServiceCharge?: ServiceCharge;
  public form = this.initForm();
  public isLoading = false;
  private subscriptions: Subscription[] = [];

  @Input() serviceCharges: ServiceCharge[] = [];

  get selectedServiceCharge(): ServiceCharge | undefined {
    return this._selectedServiceCharge;
  }

  @Input()
  set selectedServiceCharge(value: ServiceCharge | undefined) {
    this._selectedServiceCharge = value;
    if (value) {
      this.form.patchValue({
        id: value.id,
        name: value.name,
        amount: value.amount,
        amountUnit: value.amountUnit,
        applyPer: value.applyPer,
        capAmount: value.capAmount,
        capAmountUnit: value.capAmountUnit,
      });
      this.form.get('id')?.disable();
    }
  }

  private initForm(): FormGroup {
    return this.fb.group({
      id: ['', [Validators.required]],
      name: ['', [Validators.required]],
      amount: [0, [Validators.required, Validators.min(0)]],
      amountUnit: ['USD', [Validators.pattern(/^USD|percent$/)]],
      applyPer: ['ticket', [Validators.pattern(/^ticket|transaction$/)]],
      capAmount: [0, [Validators.required, Validators.min(0)]],
      capAmountUnit: ['tickets', [Validators.pattern(/^tickets|USD$/)]],
    });
  }

  getDescriptionPreview(): string {
    if (!this.form?.value) {
      return '';
    }

    const formValue = this.form.value;
    return `${formValue.amount} ${formValue.amountUnit === 'percent' ? '%' : 'USD'} per ${formValue.applyPer}, up to ${formValue.capAmount} ${formValue.capAmountUnit}`;
  }

  save(): void {
    if (this.form.invalid) {
      this.markFormGroupTouched(this.form);
      return;
    }

    const serviceCharges = this.serviceCharges;

    const isEdit = !!this.selectedServiceCharge;
    const id = isEdit ? this.selectedServiceCharge.id : this.form.value.id;
    const newSc = { ...this.form.value, id };

    const duplicatedServiceCharges = serviceCharges.filter(
      sc => sc.id.trim() === id.trim() && !isEdit
    );

    if (duplicatedServiceCharges.length > 0) {
      this.toastr.error('Service charge already exists');
      return;
    }

    if (id.length > 20) {
      this.toastr.error(
        'Service charge ID must be less than or equal to 20 characters'
      );
      return;
    }

    this.isLoading = true;

    const newServiceChargesCalc = isEdit
      ? serviceCharges.map(sc => (sc.id.trim() === id.trim() ? newSc : sc))
      : serviceCharges.concat(newSc);

    const newServiceCharges =
      newServiceChargesCalc.length > 0 ? newServiceChargesCalc : [newSc];

    console.log('DEBUG: save()', {
      isEdit,
      id,
      newSc,
      serviceChargesLength: serviceCharges.length,
      newServiceChargesLength: newServiceCharges.length,
      newServiceCharges,
    });

    this.serviceChargesService
      .createOrUpdateServiceCharge(newServiceCharges)
      .subscribe({
        next: (res: ApiResponse<ServiceCharge[]>) => {
          this.isLoading = false;
          this.toastr.success('Service charges saved successfully');
          this.dialogRef.close(res.data);
        },
        error: error => {
          this.isLoading = false;
          this.toastr.error(error?.error?.error?.message || 'Unknown Error');
        },
      });
  }

  dismissModal(): void {
    this.dialogRef.close();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  public ngOnInit(): void {
    const applyPerSubscription = this.form
      .get('applyPer')
      ?.valueChanges.subscribe((applyPer: string) => {
        if (applyPer === 'transaction') {
          const capAmountUnitControl = this.form.get('capAmountUnit');
          if (capAmountUnitControl?.value !== 'USD') {
            capAmountUnitControl?.setValue('USD');
            this.toastr.info(
              'Cap amount unit set to USD when applying per transaction.'
            );
          }
        }
      });

    this.subscriptions.push(applyPerSubscription);
  }

  public ngOnDestroy(): void {
    this.subscriptions?.forEach(subscription => subscription.unsubscribe());
  }
}
